import { TechnologyDropdown } from 'domain/shared/career';
import { PositionDropdown } from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import { ClearAfterUnmountWrapper, EnumDropdown, NativeLanguageDropdown, RadioButtonGroup } from 'framework/form';
import type { FormFieldsData } from 'framework/form';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import type { CreateEditFormField, CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import { DECISION_LABELS, REJECTION_REASONS } from '../constants';
import type { Decision } from '../constants';
import applicationDecisionValidation from '../validation/applicationDecisionValidation';
import { getTechnologyNameFilter } from './technologyNameFilter';

type ApplicationDecisionDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

const WITHDRAWAL_REASONS = {
  ANOTHER_OFFER: 'Accepted another offer',
  SALARY_EXPECTATIONS: 'Salary expectations',
  LOST_INTEREST: 'Lost interest in the role',
  PERSONAL_REASONS: 'Personal reasons',
  NO_RESPONSE: 'No response from the candidate',
};

const DECISION_OPTIONS = Object.entries(DECISION_LABELS).map(([value, label]) => ({ value, label }));

const DECISION_FIELD: CreateEditFormField = {
  id: 'decisionType',
  size: { xs: 12, md: 12, xl: 12 },
  content: <RadioButtonGroup source="decision" label="" options={DECISION_OPTIONS} />,
};

const SPECIALIZATION_FIELD: CreateEditFormField = {
  id: 'specialization',
  content: <PositionDropdown source="specialization" label="Specialization" isValueEqLabel isRequired />,
};

const LEVEL_FIELD: CreateEditFormField = {
  id: 'level',
  content: <EnumDropdown source="level" label="Level" options={EMPLOYEE_COMPETENCY_LEVEL} isRequired />,
};

const DECISION_FIELDS: Record<Decision, CreateEditFormField> = {
  INTAKE: {
    id: 'nativeLanguage',
    content: (
      <ClearAfterUnmountWrapper>
        <NativeLanguageDropdown source="nativeLanguage" label="Native language" isRequired />
      </ClearAfterUnmountWrapper>
    ),
  },
  REJECT: {
    id: 'rejectionReason',
    size: { xs: 12, md: 8, xl: 8 },
    content: (
      <ClearAfterUnmountWrapper>
        <EnumDropdown
          source="rejectionReason"
          label="Rejection Reason"
          placeholder="Select rejection reason"
          options={REJECTION_REASONS}
          isRequired
        />
      </ClearAfterUnmountWrapper>
    ),
  },
  WITHDRAWAL: {
    id: 'withdrawalReason',
    size: { xs: 12, md: 8, xl: 8 },
    content: (
      <ClearAfterUnmountWrapper>
        <EnumDropdown
          source="withdrawalReason"
          label="Withdrawal Reason"
          placeholder="Select withdrawal reason"
          options={WITHDRAWAL_REASONS}
          isRequired
        />
      </ClearAfterUnmountWrapper>
    ),
  },
};

const getDecision = ({ decision }: FormFieldsData): Decision =>
  DECISION_OPTIONS.some(option => option.value === decision) ? (decision as Decision) : 'INTAKE';

const getDecisionFormSections = (data: FormFieldsData): CreateEditFormSection[] => {
  const specialization = data.specialization as string | undefined;
  const technologyFilter = getTechnologyNameFilter(specialization);

  const technologyFields: CreateEditFormField[] = technologyFilter
    ? [
        {
          id: 'technology',
          content: (
            <ClearAfterUnmountWrapper>
              <TechnologyDropdown
                source="technology"
                label="Technology"
                customReduce={technologyFilter}
                deps={[specialization]}
              />
            </ClearAfterUnmountWrapper>
          ),
        },
      ]
    : [];

  return [
    { id: 'decision', fields: [DECISION_FIELD] },
    { id: 'candidateProfile', fields: [SPECIALIZATION_FIELD, ...technologyFields, LEVEL_FIELD] },
    { id: 'outcome', fields: [DECISION_FIELDS[getDecision(data)]] },
  ];
};

const ApplicationDecisionDrawer = ({ isOpen, onClose }: ApplicationDecisionDrawerProps) => (
  <CreateEditFormDrawer
    isOpen={isOpen}
    onClose={onClose}
    title="Make decision on new application"
    header="Make decision on next steps and update candidate profile"
    initialData={{ decision: 'INTAKE' }}
    getValidationSchemaFunc={() => Promise.resolve(applicationDecisionValidation)}
    isCreateRequest
    submitFormFunc={async () => {}}
    afterSubmit={{ execute: onClose, message: 'Decision saved.' }}
    submitButtonTitle={data => DECISION_LABELS[getDecision(data)]}
    handleDirty={false}
    getSections={getDecisionFormSections}
  />
);

export default ApplicationDecisionDrawer;
